import React, { useState } from "react";
import { Flag, Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

const REASONS = [
    { value: "not-srt", label: "Not an SRT / R/T (V6 / base trim)" },
    { value: "spam", label: "Spam" },
    { value: "scam", label: "Scam / suspicious" },
    { value: "wrong-category", label: "Wrong category / mis-listed" },
    { value: "duplicate", label: "Duplicate listing" },
    { value: "harassment", label: "Harassment / offensive" },
    { value: "other", label: "Other" },
];

export default function ReportButton({ targetType, targetId, className = "", testid }) {
    const [open, setOpen] = useState(false);
    const [reason, setReason] = useState("not-srt");
    const [notes, setNotes] = useState("");
    const [busy, setBusy] = useState(false);
    const { user } = useAuth();
    const navigate = useNavigate();

    async function submit() {
        if (!user) {
            navigate("/login");
            return;
        }
        setBusy(true);
        try {
            await api.post("/reports", {
                target_type: targetType,
                target_id: targetId,
                reason,
                notes,
            });
            toast.success("Reported. Our admins will review shortly.");
            setOpen(false);
            setNotes("");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    }

    return (
        <>
            <button
                type="button"
                onClick={() => setOpen(true)}
                data-testid={testid || `report-${targetType}-btn`}
                className={`inline-flex items-center gap-1.5 text-onyx-500 hover:text-hellcat text-xs label-caps ${className}`}
                style={{ transition: "color 0.15s" }}
            >
                <Flag className="w-3.5 h-3.5" /> Report
            </button>
            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    className="surface border-onyx-600 rounded-md max-w-md"
                    data-testid="report-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="headline text-2xl">Report</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Reason</span>
                            <select
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                data-testid="report-reason"
                                className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat"
                            >
                                {REASONS.map((r) => (
                                    <option key={r.value} value={r.value}>
                                        {r.label}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="flex flex-col gap-1.5">
                            <span className="label-caps">Details (optional)</span>
                            <textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                data-testid="report-notes"
                                maxLength={800}
                                className="w-full bg-onyx-700 border border-onyx-600 rounded-sm px-3 py-2.5 text-sm text-white focus:outline-none focus:border-hellcat h-24"
                                placeholder="Give the admins any context that helps."
                            />
                        </label>
                    </div>
                    <DialogFooter>
                        <button
                            onClick={submit}
                            disabled={busy}
                            data-testid="report-submit"
                            className="btn-red rounded-sm px-4 py-2.5 inline-flex items-center gap-2 disabled:opacity-60"
                        >
                            <Send className="w-4 h-4" /> Submit report
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );
}
